import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  ButtonBase,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useLanguage } from "./context/LanguageContext";
import { getLanguagePrefix, translatePath } from "./routing/localizedPaths";
import BrandLogo from "./components/BrandLogo";

const navCopy = {
  no: {
    items: [
      { label: "Tjenester", path: "/no/services" },
      { label: "Produkter", path: "/no/produkter" },
      { label: "Case", path: "/no/case" },
      { label: "Om", path: "/no/om" },
      { label: "Kontakt", path: "/no/kontakt" },
    ],
    cta: "Book en prat",
    homeLabel: "Gå til forsiden",
    homeTooltip: "Forside",
    menuOpen: "Åpne meny",
    menuClose: "Lukk meny",
    primaryNav: "Hovednavigasjon",
    mobileNav: "Mobilnavigasjon",
    langSwitch: "Bytt språk",
  },
  en: {
    items: [
      { label: "Services", path: "/en/services" },
      { label: "Products", path: "/en/products" },
      { label: "Case", path: "/en/case" },
      { label: "About", path: "/en/om" },
      { label: "Contact", path: "/en/kontakt" },
    ],
    cta: "Book a call",
    homeLabel: "Go to homepage",
    homeTooltip: "Home",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    primaryNav: "Primary navigation",
    mobileNav: "Mobile navigation",
    langSwitch: "Change language",
  },
};

const homeButtonSx = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 2,
  color: "text.primary",
  textDecoration: "none",
  "&:hover": { backgroundColor: "grey.100" },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const materialTheme = useTheme();
  const isMobile = useMediaQuery(materialTheme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const copy = navCopy[language] ?? navCopy.en;
  const homePath = getLanguagePrefix(language);
  const ctaPath = language === "no" ? "/no/book" : "/en/book";

  const isActive = (path) => location.pathname === path;
  const toggleDrawer = () => setMobileOpen((prev) => !prev);

  // Switch locale while staying on the equivalent page. LanguageRouteSync
  // reads the new /no|/en prefix off the URL and updates the context, so we
  // only need to navigate to the translated path.
  const changeLanguageTo = (code) => {
    if (code === language) return;
    navigate(translatePath(location.pathname, code));
  };

  const renderLanguageSwitch = (onSelect) => (
    <Box
      role="group"
      aria-label={copy.langSwitch}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 999,
        overflow: "hidden",
      }}
    >
      {[
        { code: "no", label: "NO", aria: "Norsk" },
        { code: "en", label: "EN", aria: "English" },
      ].map((opt) => {
        const selected = language === opt.code;
        return (
          <ButtonBase
            key={opt.code}
            onClick={() => onSelect(opt.code)}
            aria-label={opt.aria}
            aria-current={selected ? "true" : undefined}
            sx={{
              px: 1.25,
              py: 0.5,
              minHeight: { xs: 44, md: 36 },
              fontSize: "0.8rem",
              fontWeight: 700,
              letterSpacing: "0.03em",
              color: selected ? "primary.main" : "text.secondary",
              backgroundColor: selected ? "rgba(14,124,123,0.10)" : "transparent",
              "&:hover": { backgroundColor: selected ? "rgba(14,124,123,0.14)" : "grey.100" },
              "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
            }}
          >
            {opt.label}
          </ButtonBase>
        );
      })}
    </Box>
  );

  const drawer = (
    <Box
      component="nav"
      aria-label={copy.mobileNav}
      sx={{
        textAlign: "center",
        pt: 2,
        // Respect iOS safe-area insets so the bottom CTA isn't behind the
        // home indicator and the top isn't behind the notch when the
        // drawer fills the viewport.
        pb: "calc(env(safe-area-inset-bottom, 0px) + 8px)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Tooltip title={copy.homeTooltip} placement="bottom">
        <ButtonBase
          component={Link}
          to={homePath}
          onClick={toggleDrawer}
          aria-label={copy.homeLabel}
          sx={{
            ...homeButtonSx,
            justifyContent: "center",
            my: 2.5,
            px: 1.25,
            py: 1,
          }}
        >
          <BrandLogo size="md" align="center" />
        </ButtonBase>
      </Tooltip>
      <List sx={{ flexGrow: 1 }}>
        {copy.items.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={toggleDrawer}
            aria-current={isActive(item.path) ? "page" : undefined}
            sx={{
              justifyContent: "center",
              // Tap targets >=44px tall (WCAG 2.5.5).
              minHeight: 48,
              py: 1.25,
              color: isActive(item.path) ? "primary.main" : "text.primary",
            }}
          >
            <ListItemText
              primary={item.label}
              sx={{
                textAlign: "center",
                "& .MuiTypography-root": {
                  fontWeight: isActive(item.path) ? 700 : 500,
                  fontSize: "1rem",
                },
              }}
            />
          </ListItem>
        ))}
      </List>
      <Box sx={{ px: 2, pb: 1.5, display: "flex", justifyContent: "center" }}>
        {renderLanguageSwitch((code) => {
          changeLanguageTo(code);
          toggleDrawer();
        })}
      </Box>
      <Box sx={{ px: 2, pb: 3 }}>
        <Button
          component={Link}
          to={ctaPath}
          onClick={toggleDrawer}
          variant="contained"
          fullWidth
          sx={{ textTransform: "none", fontWeight: 600, py: 1.4, minHeight: 44 }}
        >
          {copy.cta}
        </Button>
      </Box>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: "rgba(255,255,255,0.88)",
        backdropFilter: "blur(18px)",
        borderBottom: "1px solid",
        borderColor: "rgba(216,224,234,0.86)",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 0, sm: 1 } }}>
          <Tooltip title={copy.homeTooltip} placement="bottom">
            <ButtonBase
              component={Link}
              to={homePath}
              aria-label={copy.homeLabel}
              sx={{
                ...homeButtonSx,
                px: { xs: 0.75, sm: 1 },
                py: 0.75,
              }}
            >
              <BrandLogo size={isMobile ? "sm" : "md"} />
            </ButtonBase>
          </Tooltip>

          {isMobile ? (
            <IconButton
              onClick={toggleDrawer}
              aria-label={mobileOpen ? copy.menuClose : copy.menuOpen}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-drawer"
              sx={{ color: "text.primary" }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box component="nav" aria-label={copy.primaryNav} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {renderLanguageSwitch(changeLanguageTo)}
              {copy.items.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  aria-current={isActive(item.path) ? "page" : undefined}
                  sx={{
                    color: isActive(item.path) ? "primary.main" : "text.primary",
                    fontWeight: isActive(item.path) ? 700 : 500,
                    px: 1.35,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    backgroundColor: isActive(item.path) ? "rgba(14, 124, 123, 0.08)" : "transparent",
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                component={Link}
                to={ctaPath}
                variant="contained"
                sx={{ ml: 0.75, textTransform: "none", fontWeight: 600 }}
              >
                {copy.cta}
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>

      <Drawer
        id="mobile-nav-drawer"
        anchor="right"
        open={mobileOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        aria-label={copy.mobileNav}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            // Cover most of a 375px viewport but cap so the drawer feels
            // like a panel on tablets, not a full-screen takeover.
            width: { xs: "min(86vw, 320px)", sm: 320 },
            backgroundColor: "background.paper",
          },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
